import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Brain, Heart, Users, BookOpen, Home, Shield, Star, Activity,
  AlertTriangle, CheckCircle, TrendingUp, MessageSquare, Calendar,
  ChevronDown, ChevronUp, Download, User, Sparkles, Target,
  Lightbulb, Clock, Award, Smile, Frown, Meh
} from 'lucide-react';

/**
 * MonthlyReport Component
 * Comprehensive monthly diagnostic report for child development
 * 
 * Props:
 * - childName: string
 * - month: string (e.g., "June 2025")
 * - diagnostics: object containing all diagnostic categories
 * - parentReport: object - parent-specific insights
 * - childReport: object - child-friendly celebration
 * - observerReport: object - listener coaching
 * - viewMode: 'parent' | 'child' | 'observer' | 'principal'
 */

const MonthlyReport = ({
  childName = 'Child',
  month = 'This Month',
  totalCalls = 0,
  diagnostics = {},
  parentReport = {},
  childReport = {},
  observerReport = {},
  warningFlags = [],
  viewMode = 'principal',
  onExport
}) => {
  const [expandedSections, setExpandedSections] = useState({
    emotional: true,
    communication: false,
    social: false,
    academic: false,
    family: false,
    selfRegulation: false,
    interests: false,
    wellbeing: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const firstName = childName?.split(' ')[0] || 'Child';

  // Rating badge component
  const RatingBadge = ({ rating }) => {
    const colors = {
      'Excellent': 'bg-green-100 text-green-700 border-green-300',
      'Good': 'bg-blue-100 text-blue-700 border-blue-300',
      'Developing': 'bg-amber-100 text-amber-700 border-amber-300',
      'Needs Support': 'bg-red-100 text-red-700 border-red-300'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${colors[rating] || colors['Developing']}`}>
        {rating}
      </span>
    );
  };

  // Diagnostic Section Component
  const DiagnosticSection = ({ 
    id, 
    title, 
    icon: Icon, 
    color, 
    data = {}, 
    diagnosticOutput 
  }) => {
    const isExpanded = expandedSections[id];
    
    return (
      <Card className="border-0 shadow-sm overflow-hidden">
        <div 
          className={`px-4 py-3 cursor-pointer flex items-center justify-between bg-gradient-to-r ${color}`}
          onClick={() => toggleSection(id)}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">{title}</h3>
              {data.rating && <RatingBadge rating={data.rating} />}
            </div>
          </div>
          {isExpanded ? 
            <ChevronUp className="w-5 h-5 text-white" /> : 
            <ChevronDown className="w-5 h-5 text-white" />
          }
        </div>
        
        {isExpanded && (
          <CardContent className="p-4 space-y-4">
            {/* Indicators Table */}
            {data.indicators && data.indicators.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Indicator</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">What to look for</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">{firstName}'s Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.indicators.map((indicator, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2 font-medium text-gray-800">{indicator.name}</td>
                        <td className="px-3 py-2 text-gray-600">{indicator.lookFor}</td>
                        <td className="px-3 py-2 text-gray-700">{indicator.evidence}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Diagnostic Output */}
            {diagnosticOutput && (
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg p-4 border-l-4 border-gray-400">
                <p className="text-sm font-medium text-gray-700 mb-2">Diagnostic Output:</p>
                <p className="text-gray-600 italic">{diagnosticOutput}</p>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  };

  // Parent View
  const ParentView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{firstName}'s Monthly Portrait</h1>
            <p className="text-emerald-100 mt-1">📅 {month} • {totalCalls} Daily Conversations</p>
          </div>
          {onExport && (
            <Button 
              variant="secondary" 
              className="bg-white/20 hover:bg-white/30 text-white"
              onClick={() => onExport('parent')}
            >
              <Download className="w-4 h-4 mr-2" /> Export PDF
            </Button>
          )}
        </div>
      </div>

      {/* This Month's Snapshot */}
      {parentReport.snapshot && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-blue-800 flex items-center gap-2">
              <Sparkles className="w-5 h-5" /> This Month's Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-gray-700 leading-relaxed">{parentReport.snapshot}</p>
          </CardContent>
        </Card>
      )}

      {/* Bright Spots */}
      {parentReport.brightSpots && parentReport.brightSpots.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-amber-50">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <Star className="w-5 h-5" /> Bright Spots ✨
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ul className="space-y-2">
              {parentReport.brightSpots.map((spot, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{spot}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Growth Areas */}
      {parentReport.growthAreas && parentReport.growthAreas.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-purple-50">
            <CardTitle className="text-purple-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Growth Areas 🌱
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-purple-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Area</th>
                    <th className="px-3 py-2 text-left">Observation</th>
                    <th className="px-3 py-2 text-left">Support Suggestion</th>
                  </tr>
                </thead>
                <tbody>
                  {parentReport.growthAreas.map((area, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2 font-medium">{area.area}</td>
                      <td className="px-3 py-2 text-gray-600">{area.observation}</td>
                      <td className="px-3 py-2 text-purple-700">{area.suggestion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Emotional Landscape */}
      {parentReport.emotionalLandscape && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-pink-50">
            <CardTitle className="text-pink-800 flex items-center gap-2">
              <Heart className="w-5 h-5" /> Emotional Landscape ❤️
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {parentReport.emotionalLandscape.emotions && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                {Object.entries(parentReport.emotionalLandscape.emotions).map(([emotion, triggers]) => (
                  <div key={emotion} className="bg-pink-50 rounded-lg p-3">
                    <p className="font-medium text-pink-800 capitalize">{emotion}</p>
                    <p className="text-sm text-gray-600">{triggers}</p>
                  </div>
                ))}
              </div>
            )}
            {parentReport.emotionalLandscape.pattern && (
              <div className="bg-pink-100 rounded-lg p-3">
                <p className="text-sm font-medium text-pink-700">Pattern:</p>
                <p className="text-gray-700">{parentReport.emotionalLandscape.pattern}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Conversation Starters */}
      {parentReport.conversationStarters && parentReport.conversationStarters.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-cyan-50">
            <CardTitle className="text-cyan-800 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" /> Conversation Starters 💬
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ol className="space-y-2">
              {parentReport.conversationStarters.map((question, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-gray-700">"{question}"</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Recommended Actions */}
      {parentReport.recommendedActions && parentReport.recommendedActions.length > 0 && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-green-50 to-emerald-50">
          <CardHeader>
            <CardTitle className="text-green-800 flex items-center gap-2">
              <Target className="w-5 h-5" /> Recommended Actions 📋
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ol className="space-y-3">
              {parentReport.recommendedActions.map((action, idx) => (
                <li key={idx} className="flex items-start gap-3 bg-white rounded-lg p-3">
                  <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-gray-700">{action}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Child View - Celebratory
  const ChildView = () => (
    <div className="space-y-6">
      {/* Fun Header */}
      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-xl p-6 text-white text-center">
        <h1 className="text-3xl font-bold mb-2">🌟 {firstName}'s Amazing Month! 🌟</h1>
        <p className="text-white/90">{month}</p>
      </div>

      {/* Things You Did */}
      {childReport.achievements && childReport.achievements.length > 0 && (
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-400 to-cyan-400 px-4 py-3">
            <h3 className="text-white font-bold text-lg">🎉 Things You Did This Month!</h3>
          </div>
          <CardContent className="p-4">
            <ul className="space-y-2">
              {childReport.achievements.map((item, idx) => (
                <li key={idx} className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                  <span className="text-2xl">✨</span>
                  <span className="text-gray-800">{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Your Superpowers */}
      {childReport.superpowers && childReport.superpowers.length > 0 && (
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-amber-400 to-yellow-400 px-4 py-3">
            <h3 className="text-white font-bold text-lg">💪 Your Superpowers!</h3>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-3">
              {childReport.superpowers.map((power, idx) => (
                <div key={idx} className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl mb-1">{power.emoji || '⭐'}</p>
                  <p className="font-medium text-amber-800">{power.name}</p>
                  {power.description && (
                    <p className="text-sm text-gray-600 mt-1">{power.description}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cool Things You Shared */}
      {childReport.memorableShares && childReport.memorableShares.length > 0 && (
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-green-400 to-emerald-400 px-4 py-3">
            <h3 className="text-white font-bold text-lg">🗣️ Cool Things You Shared!</h3>
          </div>
          <CardContent className="p-4">
            <div className="space-y-2">
              {childReport.memorableShares.map((share, idx) => (
                <div key={idx} className="flex items-start gap-3 p-2 bg-green-50 rounded-lg">
                  <span className="text-xl">💬</span>
                  <span className="text-gray-700">{share}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Remember When... */}
      {childReport.highlights && childReport.highlights.length > 0 && (
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-pink-400 to-rose-400 px-4 py-3">
            <h3 className="text-white font-bold text-lg">📸 Remember When...</h3>
          </div>
          <CardContent className="p-4">
            <div className="space-y-2">
              {childReport.highlights.map((highlight, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-pink-50 rounded-lg">
                  <span className="text-xl">🎈</span>
                  <span className="text-gray-700">{highlight}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Next Month's Adventure */}
      {childReport.nextMonthAdventure && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-indigo-100 to-purple-100">
          <CardContent className="p-6 text-center">
            <h3 className="text-xl font-bold text-purple-800 mb-3">🚀 Next Month's Adventure!</h3>
            <p className="text-gray-700">{childReport.nextMonthAdventure}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Observer View - Coaching
  const ObserverView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Listener Effectiveness Review</h1>
            <p className="text-amber-100 mt-1">{firstName} • {month} • {totalCalls} Calls</p>
          </div>
          {onExport && (
            <Button 
              variant="secondary" 
              className="bg-white/20 hover:bg-white/30 text-white"
              onClick={() => onExport('observer')}
            >
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
          )}
        </div>
      </div>

      {/* What Worked Well */}
      {observerReport.whatWorked && observerReport.whatWorked.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-green-50">
            <CardTitle className="text-green-800 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" /> What You Did Well
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-green-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Moment</th>
                    <th className="px-3 py-2 text-left">Why it worked</th>
                  </tr>
                </thead>
                <tbody>
                  {observerReport.whatWorked.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2 font-medium text-gray-800">"{item.moment}"</td>
                      <td className="px-3 py-2 text-gray-600">{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Opportunities to Deepen */}
      {observerReport.opportunities && observerReport.opportunities.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-amber-50">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <Lightbulb className="w-5 h-5" /> Opportunities to Deepen
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-amber-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Moment</th>
                    <th className="px-3 py-2 text-left">What happened</th>
                    <th className="px-3 py-2 text-left">Alternative approach</th>
                  </tr>
                </thead>
                <tbody>
                  {observerReport.opportunities.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2 text-gray-800">{item.moment}</td>
                      <td className="px-3 py-2 text-red-600">{item.whatHappened}</td>
                      <td className="px-3 py-2 text-green-600">{item.alternative}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Child-Specific Guidance */}
      {observerReport.childGuidance && observerReport.childGuidance.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-blue-800 flex items-center gap-2">
              <User className="w-5 h-5" /> {firstName}-Specific Guidance
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ol className="space-y-3">
              {observerReport.childGuidance.map((guidance, idx) => (
                <li key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-gray-700">{guidance}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Suggested Prompts */}
      {observerReport.suggestedPrompts && observerReport.suggestedPrompts.length > 0 && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardHeader>
            <CardTitle className="text-purple-800 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" /> Prompts to Keep Ready
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid md:grid-cols-2 gap-3">
              {observerReport.suggestedPrompts.map((prompt, idx) => (
                <div key={idx} className="bg-white rounded-lg p-3 border border-purple-200">
                  <p className="text-gray-700 italic">"{prompt}"</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Principal/Full View
  const PrincipalView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Monthly Diagnostics Report</h1>
            <p className="text-slate-300 mt-1">{childName} • {month} • {totalCalls} Calls Analyzed</p>
          </div>
          <div className="flex gap-2">
            {onExport && (
              <>
                <Button 
                  variant="secondary" 
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={() => onExport('parent')}
                >
                  Parent Report
                </Button>
                <Button 
                  variant="secondary" 
                  size="sm"
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  onClick={() => onExport('child')}
                >
                  Child Report
                </Button>
                <Button 
                  variant="secondary" 
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => onExport('observer')}
                >
                  Observer Report
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Warning Flags */}
      {warningFlags && warningFlags.length > 0 && (
        <Card className="border-2 border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Warning Flags
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2">
              {warningFlags.map((flag, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-red-200">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-800">{flag.pattern}</p>
                    <p className="text-sm text-gray-600">{flag.indication}</p>
                    <p className="text-sm text-red-600 mt-1">Action: {flag.action}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Diagnostic Categories */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">Diagnostic Categories</h2>
        
        <DiagnosticSection
          id="emotional"
          title="Emotional Intelligence Profile"
          icon={Heart}
          color="from-pink-500 to-rose-500"
          data={diagnostics.emotional}
          diagnosticOutput={diagnostics.emotional?.output}
        />
        
        <DiagnosticSection
          id="communication"
          title="Communication Development"
          icon={MessageSquare}
          color="from-blue-500 to-cyan-500"
          data={diagnostics.communication}
          diagnosticOutput={diagnostics.communication?.output}
        />
        
        <DiagnosticSection
          id="social"
          title="Social-Emotional Landscape"
          icon={Users}
          color="from-purple-500 to-indigo-500"
          data={diagnostics.social}
          diagnosticOutput={diagnostics.social?.output}
        />
        
        <DiagnosticSection
          id="academic"
          title="Academic Engagement Pattern"
          icon={BookOpen}
          color="from-green-500 to-emerald-500"
          data={diagnostics.academic}
          diagnosticOutput={diagnostics.academic?.output}
        />
        
        <DiagnosticSection
          id="family"
          title="Family Attachment & Support"
          icon={Home}
          color="from-amber-500 to-orange-500"
          data={diagnostics.family}
          diagnosticOutput={diagnostics.family?.output}
        />
        
        <DiagnosticSection
          id="selfRegulation"
          title="Self-Regulation & Coping"
          icon={Shield}
          color="from-teal-500 to-cyan-500"
          data={diagnostics.selfRegulation}
          diagnosticOutput={diagnostics.selfRegulation?.output}
        />
        
        <DiagnosticSection
          id="interests"
          title="Interests & Aspirations"
          icon={Star}
          color="from-yellow-500 to-amber-500"
          data={diagnostics.interests}
          diagnosticOutput={diagnostics.interests?.output}
        />
        
        <DiagnosticSection
          id="wellbeing"
          title="Wellbeing & Health Patterns"
          icon={Activity}
          color="from-red-500 to-pink-500"
          data={diagnostics.wellbeing}
          diagnosticOutput={diagnostics.wellbeing?.output}
        />
      </div>

      {/* Stakeholder Reports Preview */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-green-50 cursor-pointer hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-emerald-800">Parent Report</h3>
            </div>
            <p className="text-sm text-gray-600">Monthly Child Portrait with insights, bright spots, and conversation starters</p>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-pink-50 cursor-pointer hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <Smile className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-purple-800">Child Report</h3>
            </div>
            <p className="text-sm text-gray-600">Celebratory "Your Month" highlights with superpowers and achievements</p>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-orange-50 cursor-pointer hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-amber-800">Observer Report</h3>
            </div>
            <p className="text-sm text-gray-600">Effectiveness review with coaching guidance and improvement areas</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Render based on view mode
  return (
    <div className="monthly-report">
      {viewMode === 'parent' && <ParentView />}
      {viewMode === 'child' && <ChildView />}
      {viewMode === 'observer' && <ObserverView />}
      {viewMode === 'principal' && <PrincipalView />}
    </div>
  );
};

export default MonthlyReport;
